<template>
  <div class="iv-table">
    <div class="iv-table-scroll">
      <div class="iv-table-inner">
        <!-- Header -->
        <div class="iv-thead">
          <span>Appointment</span>
          <span>Patient</span>
          <span>Insurance</span>
          <span>Remaining benefits / deductible</span>
          <span>Last verified</span>
          <span>Status</span>
          <span></span>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="iv-table-loading">
          <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          Loading appointments…
        </div>

        <!-- Empty -->
        <div v-else-if="!rows.length" class="iv-table-empty">
          <div class="iv-empty-icon"><i class="fa fa-search" aria-hidden="true"></i></div>
          <div class="iv-empty-title">No appointments match</div>
          <div class="iv-empty-sub">Try a different date range or clear your filters.</div>
        </div>

        <!-- Rows -->
        <template v-else>
          <div
            v-for="row in rows"
            :key="rowKey(row)"
            class="iv-trow"
            @click="$emit('open-row', row)"
          >
            <!-- Appointment -->
            <div class="iv-cell-appt">
              <div class="iv-appt-time">{{ apptTime(row.appointment.aptDateTime) }}</div>
              <div class="iv-appt-label">{{ apptRelativeLabel(row.appointment.aptDateTime, timezone) }}</div>
              <!--
                Only ever shown for a completed visit. Without it, a row from the
                "+ Completed" lookup is indistinguishable from a genuine upcoming one, and
                the whole point of that mode is that these two mean different things.
                Absent on older row sets (aptStatus null), which is why this tests === 2
                rather than !== 1.
              -->
              <span v-if="row.appointment.aptStatus === 2" class="iv-appt-done">Completed</span>
            </div>

            <!-- Patient -->
            <div class="iv-cell-patient">
              <span class="iv-avatar iv-avatar--patient">
                {{ patientInitials(row.patient.fName, row.patient.lName) }}
              </span>
              <div class="iv-patient-meta">
                <div class="iv-patient-name">{{ patientName(row.patient) }}</div>
                <div class="iv-patient-dob" v-if="row.patient.dob">
                  DOB {{ row.patient.dob | dateformat('MM/DD/YYYY') }}
                </div>
              </div>
            </div>

            <!-- Insurance -->
            <div class="iv-cell-ins">
              <template v-if="row.insurance.hasInsurance">
                <span class="iv-avatar iv-avatar--carrier" :style="carrierAvatarStyle(row.insurance.carrierName)">
                  {{ carrierInitials(row.insurance.carrierName) }}
                </span>
                <div class="iv-ins-meta">
                  <div class="iv-ins-name">{{ row.insurance.carrierName || 'Unknown carrier' }}</div>
                  <div class="iv-ins-member" v-if="row.insurance.memberId">{{ row.insurance.memberId }}</div>
                  <!-- OD data issue (lib/preflight.js): this record can't be checked
                       until it's fixed. Same pill idiom as the warning pills — short
                       label inline, the backend's verbatim `detail` on hover. -->
                  <span
                    v-if="dataIssueFor(row)"
                    class="iv-data-pill"
                    :title="dataIssueFor(row).detail"
                  >
                    <i class="fa" :class="dataIssueIcon(row)" aria-hidden="true"></i>
                    {{ dataIssueLabel(row) }}
                  </span>
                </div>
              </template>
              <span v-else class="iv-ins-none">No insurance</span>

              <!-- Secondary plan (patplan.Ordinal = 2), when the patient carries one.
                   Stacked on the SAME row: the office thinks in appointments, not plan
                   slots, and both plans matter for one visit. Absent for ~97% of
                   patients, and `secondary` is simply missing on an older backend. -->
              <div v-if="row.secondary && row.secondary.insurance.hasInsurance" class="iv-ins-secondary">
                <span class="iv-ins-2nd-tag">2nd</span>
                <div class="iv-ins-meta">
                  <div class="iv-ins-name">{{ row.secondary.insurance.carrierName || 'Unknown carrier' }}</div>
                  <div class="iv-ins-member" v-if="row.secondary.insurance.memberId">
                    {{ row.secondary.insurance.memberId }}
                  </div>
                  <span
                    v-if="row.secondary.insurance.dataIssue"
                    class="iv-data-pill"
                    :title="row.secondary.insurance.dataIssue.detail"
                  >
                    <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                    Fix in Open Dental
                  </span>
                  <span
                    v-if="secondaryStatus(row)"
                    class="iv-ins-2nd-status"
                    :style="{ color: secondaryStatus(row).color }"
                    :title="secondaryStatus(row).label"
                  >
                    <i class="fa" :class="secondaryStatus(row).icon" aria-hidden="true"></i>
                    {{ secondaryStatus(row).label }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Remaining benefits / deductible.
                 Reads row.benefits, which the backend assembles from two narrow
                 JSON_EXTRACTs of the stored Summary — the blob itself runs 28KB–192KB and
                 could never be shipped per row. An older backend simply omits the field and
                 every cell falls back to the em-dash, so this degrades rather than breaks.

                 ⛔ The wording rules live in benefitCellView (constants.js), NOT here: they
                 encode which claims the payer actually supports (an amount without a
                 reported usage must never render as "left"), and a template is the wrong
                 place for a rule that has to be testable. -->
            <div class="iv-cell-benefits">
              <div class="iv-ben-block" :class="{ 'iv-ben--muted': benefitsFor(row).muted }">
                <div class="iv-ben-max">{{ benefitsFor(row).maxLine }}</div>
                <div class="iv-ben-ded" v-if="benefitsFor(row).dedLine">
                  {{ benefitsFor(row).dedLine }}
                </div>
              </div>

              <!-- The secondary plan's OWN numbers, stacked to mirror the Insurance cell.
                   Never the primary's figures under the secondary's name — coordination of
                   benefits is exactly where that error costs the patient money. -->
              <div
                v-if="row.secondary && row.secondary.insurance.hasInsurance && !secondaryBenefitsFor(row).empty"
                class="iv-ben-block iv-ben-block--2nd"
                :class="{ 'iv-ben--muted': secondaryBenefitsFor(row).muted }"
              >
                <span class="iv-ins-2nd-tag">2nd</span>
                <div>
                  <div class="iv-ben-max">{{ secondaryBenefitsFor(row).maxLine }}</div>
                  <div class="iv-ben-ded" v-if="secondaryBenefitsFor(row).dedLine">
                    {{ secondaryBenefitsFor(row).dedLine }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Last verified -->
            <div class="iv-cell-lv">
              <span class="iv-lv-label" :class="lvClass(row)" :title="lvTitle(row)">
                {{ lvLabel(row) }}
              </span>
              <div v-if="lvNote(row)" class="iv-lv-note" :title="lvTitle(row)">{{ lvNote(row) }}</div>
            </div>

            <!-- Status + warnings -->
            <div class="iv-cell-status">
              <div class="iv-status" :style="{ color: statusFor(row).color }">
                <i class="fa" :class="statusFor(row).icon" aria-hidden="true"></i>
                <span class="iv-status-label">{{ statusFor(row).label }}</span>
              </div>
              <div v-if="warningsFor(row).length" class="iv-warn-pills">
                <span
                  v-for="(w, i) in warningsFor(row)"
                  :key="i"
                  class="iv-warn-pill"
                  :title="w.detail || ''"
                >
                  <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                  {{ w.title || w.code }}
                </span>
              </div>
              <!--
                The payer was unreachable, and the nightly runner will try again by itself.
                Without this the row reads as a dead end and the front desk clicks Verify —
                paying for a check that was already coming free.

                ⚠ Muted grey, NOT the amber of .iv-warn-pill: nothing here needs fixing, and
                dressing it as a warning would send someone to repair a record that is fine.
              -->
              <div v-if="retryNoticeFor(row)" class="iv-retry-note">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                {{ retryNoticeFor(row).text }}
              </div>
            </div>

            <!-- Action -->
            <div class="iv-cell-action" @click.stop>
              <!--
                The office excluded this carrier — show the state instead of a button that
                can only lead to a refusal.

                ⚠ A COURTESY, NOT THE GATE. The server refuses this check in
                runEligibilityCheck STEP 1.4 regardless of what this table renders, and it
                re-reads the setting on every check. Never treat this v-if as the thing
                protecting the spend: the row snapshot can be minutes stale, and the same
                lesson already cost us once with the Super-Admin lock that was UI-only.
              -->
              <span v-if="row.excluded" class="iv-excluded-tag" title="Excluded from electronic verification — change this in Precheck settings → Carriers">
                Excluded
              </span>
              <button
                v-else-if="row.insurance.hasInsurance"
                type="button"
                class="iv-verify-btn"
                :disabled="isVerifying(row)"
                @click="$emit('verify-row', row)"
              >
                <i v-if="isVerifying(row)" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                {{ isVerifying(row) ? 'Checking…' : 'Verify' }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {
  statusForRow,
  retryNoticeFor,
  patientInitials,
  patientName,
  carrierInitials,
  carrierColor,
  apptTime,
  apptRelativeLabel,
  relativeFromNow,
  formatCheckedAt,
  lastCheckNote,
  rowDataIssue,
  chipForCode,
  benefitCellView,
} from './constants.js';

export default {
  name: 'VerificationTable',
  props: {
    // Rows already client-filtered by the page.
    rows: {
      type: Array,
      default: () => [],
    },
    // Site timezone (GENERAL_TIMEZONE) for the Last-verified column.
    timezone: {
      type: String,
      default: '',
    },
    // { autoPrecheckEnabled, lookaheadDays } — what the NIGHTLY RUNNER will do, straight off
    // the dashboard response so it can never disagree with the run it describes.
    // ⛔ Defaults to null, not {}: absent must mean "say nothing", never "assume automation
    // is on". A wrong retry promise is worse than no message at all.
    automation: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Per-patient verify state map keyed by patNum → { loading, message, type }.
    // Drives the per-row Verify button spinner/disable while a check is in flight.
    verifyState: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    // expose pure helpers to the template
    patientInitials,
    patientName,
    carrierInitials,
    apptTime,
    apptRelativeLabel,

    isVerifying(row) {
      const pat = row && row.patient && row.patient.patNum;
      if (pat === null || pat === undefined) return false;
      const st = this.verifyState[pat];
      return !!(st && st.loading);
    },

    statusFor(row) {
      return statusForRow(row);
    },

    /**
     * "Couldn't reach <Carrier> — will retry automatically tonight", or null.
     *
     * All the judgement lives in constants.js retryNoticeFor: transient-only, automation-on
     * only, and window-aware so it never says "tonight" about a row the runner will not
     * touch for weeks. This just hands it the row and the automation facts the server sent.
     */
    retryNoticeFor(row) {
      return retryNoticeFor(row, this.automation);
    },

    // The secondary plan's OWN status. `row.secondary` carries the same
    // { insurance, verification } shape as a row, so the shared presenter works on it
    // unchanged. Null when the patient has one plan (or the backend predates secondary
    // support and simply doesn't send the field).
    secondaryStatus(row) {
      if (!row || !row.secondary || !row.secondary.insurance || !row.secondary.insurance.hasInsurance) return null;
      return statusForRow(row.secondary);
    },

    // ── Remaining benefits / deductible ──────────────────────────────────
    // `row.benefits` is `{ annualMaximum, deductible }` from dashboard-assemble. A backend
    // that predates the column omits it entirely, and benefitCellView answers with the
    // em-dash cell — so an out-of-step deploy degrades quietly instead of throwing.
    //
    // Staleness only MUTES the numbers. Hiding them would throw away the best figure
    // anyone has over an age the LAST VERIFIED column already states plainly.
    benefitsFor(row) {
      return benefitCellView(row && row.benefits, { isStale: !!(row && row.isStale) });
    },

    // The secondary plan's own benefits. It inherits the row's staleness because the row's
    // isStale is primary-driven and the secondary carries no separate freshness verdict —
    // muting both together is the conservative reading.
    secondaryBenefitsFor(row) {
      const sec = row && row.secondary;
      return benefitCellView(sec && sec.benefits, { isStale: !!(row && row.isStale) });
    },

    // ── OD data issue (Slice: preflight gate) ────────────────────────────
    // `{ code, detail }` from dashboard-assemble's screenMemberId, or null.
    dataIssueFor(row) {
      return rowDataIssue(row);
    },
    dataIssueLabel(row) {
      const issue = rowDataIssue(row);
      return issue ? chipForCode(issue.code).label : '';
    },
    dataIssueIcon(row) {
      const issue = rowDataIssue(row);
      return issue ? chipForCode(issue.code).icon : 'fa-exclamation-triangle';
    },

    warningsFor(row) {
      return (row.verification && row.verification.warnings) || [];
    },
    rowKey(row) {
      const apt = (row.appointment && row.appointment.aptNum) || '';
      const pat = (row.patient && row.patient.patNum) || '';
      return `${pat}-${apt}`;
    },
    carrierAvatarStyle(name) {
      return { background: carrierColor(name) };
    },
    lvLabel(row) {
      if (row.neverVerified) return 'Not verified';
      return relativeFromNow(row.lastVerified, this.timezone) || '—';
    },
    lvTitle(row) {
      const verified = row.neverVerified
        ? 'This patient has not been verified yet'
        : `Last verified ${formatCheckedAt(row.lastVerified, this.timezone)}`;
      // lastChecked is additive on the backend — fall back to the latest check's own time.
      const checkedAt = row.lastChecked || (row.verification && row.verification.checkedAt) || null;
      const checked = checkedAt ? formatCheckedAt(checkedAt, this.timezone) : '';
      return checked ? `${verified} · Last checked ${checked}` : verified;
    },
    lvNote(row) {
      return lastCheckNote({
        verification: row.verification,
        lastVerified: row.lastVerified,
        lastChecked: row.lastChecked,
        carrierName: row.insurance && row.insurance.carrierName,
        tz: this.timezone,
      });
    },
    lvClass(row) {
      if (row.neverVerified) return 'iv-lv--muted';
      if (row.isStale) return 'iv-lv--stale';
      if (row.isFresh) return 'iv-lv--fresh';
      return 'iv-lv--normal';
    },
  },
};
</script>

<style lang="scss" scoped>
// Shared column template — keep header and rows in lockstep.
// Tracks: Appointment · Patient · Insurance · Remaining benefits · Last verified · Status · Action
$iv-cols: 150px 1.4fr 1.3fr 1.4fr 130px 1.5fr 120px;

.iv-table-scroll {
  overflow-x: auto;
}

.iv-table-inner {
  // Raised from 900px with the benefits column (2026-08-14) — below this the fr tracks
  // squeeze money onto three lines. The table scrolls horizontally rather than dropping
  // columns, so a narrow viewport still reaches everything.
  min-width: 1080px;
}

.iv-thead {
  display: grid;
  grid-template-columns: $iv-cols;
  gap: 12px;
  padding: 12px 18px;
  background: #fafbfb;
  border-bottom: 1px solid #dfe4e3;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #717977;
}

.iv-trow {
  display: grid;
  grid-template-columns: $iv-cols;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #dfe4e3;
  align-items: center;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: #fafbfb;
  }
}

.iv-appt-time {
  font-weight: 700;
  font-size: 14px;
  color: #0c0f0e;
}

.iv-appt-label {
  font-size: 12px;
  color: #9ca4a2;
  margin-top: 2px;
}

// Neutral grey on purpose — a completed visit is context, not a problem. The amber and red
// in this table are reserved for things the front desk has to act on.
.iv-appt-done {
  display: inline-block;
  margin-top: 4px;
  padding: 1px 7px;
  background: #eef1f0;
  color: #5d6664;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.iv-cell-patient {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.iv-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-weight: 700;
  color: #ffffff;

  &--patient {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #cdede2;
    color: #0b5c49;
    font-size: 12px;
  }
  &--carrier {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 800;
  }
}

.iv-patient-meta {
  min-width: 0;
}

.iv-patient-name {
  font-weight: 700;
  font-size: 14px;
  color: #0b5c49;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iv-patient-dob {
  font-size: 12px;
  color: #9ca4a2;
  margin-top: 1px;
}

.iv-cell-ins {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.iv-ins-meta {
  min-width: 0;
}

.iv-ins-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #0c0f0e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iv-ins-member {
  font-size: 11.5px;
  color: #9ca4a2;
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
}

.iv-ins-none {
  font-size: 13px;
  color: #9ca4a2;
}

// Secondary plan, stacked under the primary in the same cell. Deliberately quieter
// than the primary (smaller, muted, hairline rule above) so the row still reads
// primary-first at a glance — but present, because a second plan changes what the
// patient owes.
.iv-ins-secondary {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e6eae9;
  min-width: 0;

  .iv-ins-name {
    font-size: 12.5px;
    font-weight: 500;
    color: #4a5250;
  }
  .iv-ins-member {
    font-size: 11px;
  }
}

// ── Remaining benefits / deductible ──────────────────────────────────────────
// Follows the Appointment cell's two-line idiom (bold value over a muted sub-line) rather
// than inventing a third, so the row reads as one table.
.iv-cell-benefits {
  min-width: 0;
}

.iv-ben-max {
  font-weight: 700;
  font-size: 13.5px;
  color: #0c0f0e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.iv-ben-ded {
  font-size: 12px;
  color: #717977;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Stale (30+ days): still shown, but it must not read like money confirmed this morning.
// Same grey the Last-verified column uses for its muted state.
.iv-ben--muted {
  .iv-ben-max,
  .iv-ben-ded {
    color: #9ca4a2;
    font-weight: 600;
  }
}

// The secondary plan's own numbers — mirrors .iv-ins-secondary exactly so the two cells
// line up visually across the row.
.iv-ben-block--2nd {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e6eae9;
  min-width: 0;

  .iv-ben-max {
    font-size: 12.5px;
    font-weight: 500;
    color: #4a5250;
  }
  .iv-ben-ded {
    font-size: 11px;
  }
}

.iv-ins-2nd-tag {
  flex: 0 0 auto;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #6b7573;
  background: #f1f4f3;
  border-radius: 3px;
  padding: 2px 4px;
  margin-top: 1px;
}

.iv-ins-2nd-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 600;
}

// OD data-issue pill — same amber language as .iv-warn-pill (below), one size
// down since it sits inside the insurance cell rather than the status column.
.iv-data-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  max-width: 100%;
  padding: 2px 7px;
  border-radius: 20px;
  background: #fef6e7;
  border: 1px solid #fde9c8;
  color: #b45309;
  font-size: 10.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .fa { flex: none; }
}

.iv-lv-label {
  font-size: 13px;
  font-weight: 600;

  &.iv-lv--muted { color: #9ca4a2; }
  &.iv-lv--stale { color: #e5484d; }
  &.iv-lv--fresh { color: #0f7259; }
  &.iv-lv--normal { color: #717977; }
}

// What happened to a latest check that did not verify. Muted, like .iv-retry-note.
.iv-lv-note {
  margin-top: 3px;
  color: #717977;
  font-size: 11.5px;
  line-height: 1.35;
}

.iv-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.iv-status-label {
  font-weight: 700;
  font-size: 13.5px;
}

.iv-warn-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 6px;
}

.iv-warn-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 20px;
  background: #fef6e7;
  border: 1px solid #fde9c8;
  color: #b45309;
  font-size: 11px;
  font-weight: 600;
}

// "will retry automatically tonight". Neutral on purpose — the same muted language as
// .iv-excluded-tag / .iv-appt-done, which mean "context", not "problem". The amber above is
// reserved for things a human must fix; this is the opposite message, and if it looked like a
// warning the front desk would act on a row that is already handling itself.
.iv-retry-note {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-top: 6px;
  color: #717977;
  font-size: 11.5px;
  line-height: 1.35;

  .fa { flex: none; font-size: 10.5px; }
}

.iv-cell-action {
  text-align: right;
}

// Not a button: an excluded carrier has no action to offer here. Muted so it reads as a
// state rather than a disabled control someone might keep clicking.
.iv-excluded-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  background: #f1f4f3;
  border: 1px solid #dfe4e3;
  color: #717977;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: help;
}

.iv-verify-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #c8cfcd;
  background: #ffffff;
  color: #0b5c49;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover:not(:disabled) {
    border-color: #0b5c49;
    background: #e9f6f1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.iv-table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: #717977;
  font-size: 14px;
}

.iv-table-empty {
  padding: 56px 20px;
  text-align: center;
  color: #9ca4a2;
}

.iv-empty-icon {
  font-size: 30px;
  margin-bottom: 8px;
  color: #c8cfcd;
}

.iv-empty-title {
  font-weight: 700;
  color: #545b59;
  font-size: 15px;
}

.iv-empty-sub {
  font-size: 13.5px;
  margin-top: 4px;
}
</style>
